import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AgentAction,
  Artifact,
  ChatSession,
  ChatState,
  Feedback,
  InteractionRequest,
  LoadingState,
  MessageAttachment,
  TodoItem,
  DetectedEntity,
  EntityType,
} from "../types";
import { v4 as uuidv4 } from "uuid";
import { addOrUpdateAction } from "../utils";
import { Citation } from "../../shared/types";

interface AddMessagePayload {
  sessionId: string;
  id: string;
  content: string;
  role: "user" | "assistant";
  timestamp: number;
  actions?: AgentAction[];
  interaction?: InteractionRequest;
  artifacts?: Artifact[];
  heading: string;
  citations?: Citation[];
  todos?: TodoItem[];
  attachments?: MessageAttachment[];
  confidence_str?: string;
}

interface DeleteMessagePayload {
  sessionId: string;
  messageId: string;
}

interface UpdateContextPayload {
  sessionId: string;
  context: Record<string, unknown>;
}

interface UpdateDatamateInSessionPayload {
  sessionId: string;
  datamates: string[];
}

interface UpdateEntitiesPayload {
  sessionId: string;
  entities: DetectedEntity[];
}

interface UpdateEntityTypePayload {
  sessionId: string;
  entityName: string;
  newType: EntityType;
}

export const initialState: ChatState = {
  sessions: {},
  currentSessionId: null,
  loadingState: LoadingState.UNINITIALIZED,
  error: null,
  abortController: null,
  currentActions: [],
  currentTodos: [],
};

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    createSession: (
      state,
      action: PayloadAction<{
        context?: Record<string, unknown>;
        models?: { label: string; value: string }[];
      }>,
    ) => {
      const sessionId = uuidv4();
      state.sessions[sessionId] = {
        id: sessionId,
        messages: [],
        context: action.payload.context || {},
        selectedModel: action.payload?.models?.[0]?.value || null,
      };
      state.currentSessionId = sessionId;
    },
    setSelectedModel: (
      state,
      action: PayloadAction<{ sessionId: string; model: string }>,
    ) => {
      if (state.sessions[action.payload.sessionId]) {
        state.sessions[action.payload.sessionId].selectedModel =
          action.payload.model;
      }
    },
    setCurrentSession: (state, action: PayloadAction<string | null>) => {
      state.currentSessionId = action.payload;
    },
    addMessage: (state, action: PayloadAction<AddMessagePayload>) => {
      const { sessionId, ...message } = action.payload;
      const session = state.sessions[sessionId];
      if (session) {
        const currentMessageIndex = session.messages.findIndex(
          (m) => m.id === message.id,
        );
        if (currentMessageIndex !== -1) {
          session.messages[currentMessageIndex] = {
            ...message,
            attachments: message.attachments || [],
          };
        } else {
          session.messages.push({
            ...message,
            attachments: message.attachments || [],
          });
        }
      }
    },
    updateMessageFeedback: (
      state,
      action: PayloadAction<{
        messageId: string;
        feedback: Feedback;
      }>,
    ) => {
      if (state.currentSessionId) {
        const message = state.sessions[state.currentSessionId].messages.find(
          (m) => m.id === action.payload.messageId,
        );
        if (message) {
          // TODO: Implement feedback submission
          // message.feedback = action.payload.feedback;
        }
      }
    },
    clearHistory: (state) => {
      if (state.currentSessionId) {
        state.sessions[state.currentSessionId].messages = [];
      }
    },
    deleteMessage: (state, action: PayloadAction<DeleteMessagePayload>) => {
      const { sessionId, messageId } = action.payload;
      const session = state.sessions[sessionId];
      if (session) {
        session.messages = session.messages.filter(
          (message) => message.id !== messageId,
        );
      }
    },
    setLoadingState: (
      state,
      action: PayloadAction<{
        state: LoadingState;
        sessionId: ChatState["currentSessionId"];
      }>,
    ) => {
      if (state.currentSessionId === action.payload.sessionId) {
        state.loadingState = action.payload.state;
      }
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setAbortController: (
      state,
      action: PayloadAction<AbortController | null>,
    ) => {
      state.abortController = action.payload;
    },
    addAction: (state, action: PayloadAction<AgentAction>) => {
      state.currentActions = addOrUpdateAction(
        state.currentActions,
        action.payload,
      );
    },
    clearActions: (state) => {
      state.currentActions = [];
    },
    setTodos: (state, action: PayloadAction<TodoItem[]>) => {
      state.currentTodos = action.payload;
    },
    clearTodos: (state) => {
      state.currentTodos = [];
    },
    updateContext: (state, action: PayloadAction<UpdateContextPayload>) => {
      const { sessionId, context } = action.payload;
      if (state.sessions[sessionId]) {
        state.sessions[sessionId].context = {
          ...state.sessions[sessionId].context,
          ...context,
        };
      }
    },
    clearContext: (state, action: PayloadAction<{ sessionId: string }>) => {
      const { sessionId } = action.payload;
      if (state.sessions[sessionId]) {
        state.sessions[sessionId].context = {};
      }
    },
    updateDatamateInSession: (
      state,
      action: PayloadAction<UpdateDatamateInSessionPayload>,
    ) => {
      const { sessionId, datamates } = action.payload;
      if (state.sessions[sessionId]) {
        state.sessions[sessionId].datamates = datamates;
      }
    },
    updateEntities: (state, action: PayloadAction<UpdateEntitiesPayload>) => {
      const { sessionId, entities } = action.payload;
      if (state.sessions[sessionId]) {
        state.sessions[sessionId].context = {
          ...state.sessions[sessionId].context,
          entities,
        };
      }
    },
    updateEntityType: (
      state,
      action: PayloadAction<UpdateEntityTypePayload>,
    ) => {
      const { sessionId, entityName, newType } = action.payload;
      if (state.sessions[sessionId]) {
        const entities =
          (state.sessions[sessionId].context?.entities as DetectedEntity[]) ||
          [];
        const updatedEntities = entities.map((entity) =>
          entity.name === entityName
            ? { ...entity, selected_type: newType }
            : entity,
        );
        state.sessions[sessionId].context = {
          ...state.sessions[sessionId].context,
          entities: updatedEntities,
        };
      }
    },
    setSessions: (
      state,
      action: PayloadAction<Record<string, ChatSession>>,
    ) => {
      // merge the new sessions with the existing sessions
      state.sessions = { ...state.sessions, ...action.payload };
      // If current session has messages, set the state to loaded instead of uninitialzed to show action buttons
      if (
        state.currentSessionId &&
        state.loadingState === LoadingState.UNINITIALIZED
      ) {
        const currentSession = state.sessions[state.currentSessionId];
        if (currentSession?.messages.length) {
          state.loadingState = LoadingState.LOADED;
        }
      }
    },
    resetSession: (state) => {
      state.currentSessionId = null;
      state.loadingState = LoadingState.UNINITIALIZED;
      state.error = null;
    },
  },
});

export const {
  createSession,
  setSelectedModel,
  setCurrentSession,
  addMessage,
  updateMessageFeedback,
  clearHistory,
  deleteMessage,
  setLoadingState,
  setError,
  setAbortController,
  addAction,
  clearActions,
  setTodos,
  clearTodos,
  updateContext,
  clearContext,
  updateDatamateInSession,
  updateEntities,
  updateEntityType,
  setSessions,
  resetSession,
} = chatSlice.actions;

export const chatReducer = chatSlice.reducer;
